import { moduleActionContext } from '@/store'
import { ActionContext } from 'vuex'
import { MarkerState, RootState } from '@/store/types'
import { defineModule } from 'direct-vuex'
import { Marker } from '../classes/Marker'
import { ScteMarker, ScteSegmentationType, ScteSpliceNetworkIndicator } from '../classes/ScteMarker'
import { KeyDict } from '@/types'

type MarkersContext = ActionContext<MarkerState, RootState>

const markersModule = defineModule({
  namespaced: true,
  state: {
    markers: {}
  } as MarkerState,
  actions: {
    // temporarly function, must go to the database
    // eslint-disable-next-line max-lines-per-function
    async setupHardcodedMarkers (context: MarkersContext) {
      const { commit, state } = markersModuleActionContext (context)
      if (Object.keys(state.markers).length > 0) return
      const markers: KeyDict<Marker> = {}
      markers['Game Start'] = ScteMarker
        .create({ id: 'Game Start', name: 'Game Start' }).unwrapOrFatal()
        .addSplice({
          splice_event_id: 13536,
          out_of_network_indicator: ScteSpliceNetworkIndicator.OutPoint,
          duration_flag: false,
          splice_immediate_flag: true,
          break_duration_auto_return: false,
          break_duration: 0
        }).addDescriptor({
          identifier_str: 'CUEI',
          segmentation_event_id: 0,
          segmentation_type_id: ScteSegmentationType.ProgramStart
        })

      markers['Game End'] = ScteMarker
        .create({ id: 'Game End', name: 'Game End' }).unwrapOrFatal()
        .addSplice({
          splice_event_id: 13537,
          out_of_network_indicator: ScteSpliceNetworkIndicator.InPoint,
          duration_flag: false,
          splice_immediate_flag: true,
          break_duration_auto_return: false,
          break_duration: 0
        }).addDescriptor({
          identifier_str: 'CUEI',
          segmentation_event_id: 0,
          segmentation_type_id: ScteSegmentationType.ProgramStart
        })

      markers.Program = ScteMarker
        .create({ id: 'Program', name: 'Program' }).unwrapOrFatal()
        .addSplice({
          splice_event_id: 13535,
          out_of_network_indicator: ScteSpliceNetworkIndicator.InPoint,
          duration_flag: false,
          splice_immediate_flag: true,
          break_duration_auto_return: false,
          break_duration: 0
        }).addDescriptor({
          identifier_str: 'CUEI',
          segmentation_event_id: 0,
          segmentation_type_id: ScteSegmentationType.ProviderPlacementOpportunityEnd
        })

      markers.ComRL = ScteMarker
        .create({ id: 'ComRL', name: 'ComRL' }).unwrapOrFatal()
        .addSplice({
          splice_event_id: 13534,
          out_of_network_indicator: ScteSpliceNetworkIndicator.OutPoint,
          duration_flag: true,
          splice_immediate_flag: false,
          break_duration_auto_return: true,
          break_duration: 120_000
        }).addDescriptor({
          identifier_str: 'CUEI',
          segmentation_event_id: 1073741828,
          segmentation_type_id: ScteSegmentationType.ProviderPlacementOpportunityStart
        })

      markers.Pregame = ScteMarker
        .create({ id: 'Pregame', name: 'Pregame' }).unwrapOrFatal()
        .addSplice({
          splice_event_id: 13538,
          out_of_network_indicator: ScteSpliceNetworkIndicator.OutPoint,
          duration_flag: true,
          splice_immediate_flag: false,
          break_duration_auto_return: true,
          break_duration: 150_000
        }).addDescriptor({
          identifier_str: 'CUEI',
          segmentation_event_id: 1073741828,
          segmentation_type_id: ScteSegmentationType.DistributorAdvertisementStart
        })

      markers.EOQ = ScteMarker
        .create({ id: 'EOQ', name: 'End of Quarter' }).unwrapOrFatal()
        .addSplice({
          splice_event_id: 13539,
          out_of_network_indicator: ScteSpliceNetworkIndicator.OutPoint,
          duration_flag: true,
          splice_immediate_flag: false,
          break_duration_auto_return: true,
          break_duration: 150_000
        }).addDescriptor({
          identifier_str: 'CUEI',
          segmentation_event_id: 1073741828,
          segmentation_type_id: ScteSegmentationType.DistributorAdvertisementStart
        })

      commit.update(markers)
    },
    async setMarkers (context: MarkersContext, markers: KeyDict<Marker>) {
      const { commit } = markersModuleActionContext(context)
      commit.update(markers)
    }
  },
  mutations: {
    update (state: MarkerState, markers: KeyDict<Marker>) {
      state.markers = markers
    }
  },
  getters: {
  }
})

export default markersModule
export const markersModuleActionContext = (context: MarkersContext) => moduleActionContext(context, markersModule)
